/**
 * Ukrainian dictionary — the source of truth for translation keys
 * (UA is the primary UI language; see Уточнення від команди 3).
 * `en.ts` must provide the same keys — enforced by its type annotation.
 */
// brand strings (name/tagline) are NOT dictionary keys: a module-scope
// dictionary would freeze the env-configured value at import — render sites
// call utils/branding.ts directly (lazy per-call reads)

export const uk = {
	'nav.login': 'Увійти',
	'nav.cabinet': 'Мій кабінет',
	// admin destinations — grouped in app/components/admin-nav.ts; keep every
	// `nav.*` key in this block (nav.invoices used to live down in the docgen-2
	// section, far from its siblings)
	'nav.admin': 'Розділи адміністрування',
	'nav.menu': 'Меню',
	'nav.group.people': 'Люди',
	'nav.group.learning': 'Навчання',
	'nav.group.money': 'Гроші',
	'nav.group.system': 'Система',
	'nav.applications': 'Анкети',
	'nav.participants': 'Учасники',
	'nav.courses': 'Курси',
	'nav.hints': 'Підказки кураторам',
	'nav.templates': 'Шаблони',
	'nav.import': 'Імпорт',
	'nav.paymentsInbox': 'Вхідні оплати',
	'nav.invoices': 'Рахунки',
	'nav.fops': 'ФОПи',
	'nav.salaryRates': 'Ставки',
	'nav.salary': 'Нарахування',

	// header user menu (dropdown) + global header search
	'userMenu.label': 'Меню користувача',
	'userMenu.profile': 'Профіль',
	'userMenu.logout': 'Вийти',
	'search.label': 'Пошук',

	'home.apply': 'Записатись на курс',
	'home.hint':
		'Подайте анкету — ми зв’яжемось із вами. Учасники курсів входять за кодом з пошти і бачать свої курси, розклад, оплати й відеозаписи в кабінеті.',

	'cabinet.title': 'Мій кабінет',
	'cabinet.welcome': 'Вітаємо, {name}!',
	'cabinet.empty':
		'Тут з’являться ваші курси, розклад і стан оплат — щойно ви запишетесь на перший курс.',

	'cabinet.myCourses': 'Мої курси',
	'cabinet.schedule': 'Розклад',
	'cabinet.paymentState': 'Стан оплати',
	'cabinet.videos': 'Відеозаписи',
	'cabinet.videosPlaceholder':
		'Посилання на відеозаписи з’являться тут — доступ до відео зараз надає кураторка за запитом.',
	'cabinet.notLinked':
		'Ваш акаунт ще не пов’язаний із записом учасника — якщо ви вже подавали анкету, зверніться до кураторки.',

	'participants.title': 'Учасники',
	'participants.empty': 'Поки що жодного учасника — вони з’являться з анкет.',
	'participants.new': 'Новий учасник',
	'participants.search': 'Пошук за іменем або email',
	'participants.notFound': 'Нікого не знайдено за цим запитом.',
	'participants.filter.course': 'Курс',
	'participants.filter.status': 'Статус участі',
	'participants.filter.any': 'будь-який',
	'participants.filter.apply': 'Фільтрувати',
	'bulk.title': 'Масові дії (для вибраних)',
	'bulk.op': 'Дія',
	'bulk.enroll': 'Записати на курс',
	'bulk.setStatus': 'Змінити статус участі',
	'bulk.apply': 'Застосувати',
	'bulk.selectAll': 'Вибрати всіх',

	'import.title': 'Імпорт зі Sheets',
	'import.subtitle':
		'Вставте таблицю з Google Sheets (скопіюйте діапазон із заголовками: ім’я, email, телефон, tg, оплачено). Повторний запуск того самого імпорту дублів не створює; наявні записи учасників не змінюються.',
	'import.defaultStatus': 'Статус участі для нових записів',
	'import.paste': 'Дані (TSV із Sheets або CSV)',
	'import.placeholder':
		"ім'я\temail\tтелефон\tоплачено\nМар'яна Прикладова\tm@example.com\t0671112233\t3000",
	'import.run': 'Імпортувати',
	'import.reportTitle': 'Звіт імпорту',
	'import.report.created': 'створено учасників: {count}',
	'import.report.matched': 'зіставлено з наявними: {count}',
	'import.report.enrolled': 'записано на курс: {count}',
	'import.report.payments': 'оплат створено: {count}',
	'import.report.paymentsSkipped': 'оплат пропущено (вже є): {count}',
	'import.report.errors': 'пропущені рядки ({count})',
	'import.report.line': 'рядок {line}',

	'inbox.title': 'Вхідні оплати',
	'inbox.subtitle':
		'Вставте виписку у 2 колонки через табуляцію: сума ⇥ призначення (як копіювання з таблиці). Матчер пропонує кандидатів по ПІБ у призначенні — прив’язку до оплати ви робите вручну. Рядки без табуляції чи без суми відхиляються з причиною; повторний той самий рядок пропускається.',
	'inbox.paste': 'Виписка (сума ⇥ призначення)',
	'inbox.placeholder':
		"3000\tоплата курсу Мар'яна Приклад\n2500\tвнесок за навчання Петренко Іван",
	'inbox.run': 'Розібрати',
	'inbox.reportTitle': 'Звіт розбору',
	'inbox.report.added': 'додано у чергу: {count}',
	'inbox.report.skipped': 'пропущено (вже у черзі): {count}',
	'inbox.report.rejected': 'відхилені рядки ({count})',
	'inbox.queueTitle': 'Черга неприв’язаних',
	'inbox.queueEmpty': 'Черга порожня — усі вхідні оплати розібрано.',
	'inbox.item.amount': 'Сума: {amount} грн',
	'inbox.candidates': 'Кандидати',
	'inbox.noCandidates': 'Кандидатів не знайдено.',
	'inbox.manualPath': 'ручний шлях',
	'inbox.candidateNoEnrollments': 'Кандидат без записів на курс —',
	'inbox.bindCourse': 'Курс для оплати',
	'inbox.paidAt': 'Дата оплати',
	'inbox.bind': 'Прив’язати оплату',
	'inbox.dismissReason': 'Причина відхилення (опційно)',
	'inbox.dismiss': 'Відхилити',

	'participant.name': 'ПІБ',
	'participant.email': 'Email',
	'participant.phone': 'Телефон',
	'participant.tgHandle': 'Telegram',
	'participant.social': 'ФБ / Інстаграм',
	'participant.notes': 'Нотатки',
	'participant.category': 'Категорія',
	'participant.category.general': 'загальна',
	'participant.category.military': 'військовий',
	'participant.category.volunteer': 'волонтер',
	'participant.category.dsns': 'ДСНС',
	'participant.preferredChannel': 'Канал зв’язку',
	'participant.courses': 'Курси учасника',
	'participant.noCourses': 'Ще не записаний(а) на жодний курс.',

	'courses.title': 'Курси',
	'courses.empty': 'Поки що жодного курсу — створіть перший.',
	'courses.emptyMine': 'У вас поки немає власних курсів.',
	'courses.new': 'Новий курс',
	'courses.onlyMine': 'лише мої',
	// стан фільтра в доступному імені (a11y): вимкнено vs увімкнено
	'courses.onlyMineOff': 'лише мої',
	'courses.onlyMineOn': 'лише мої (увімкнено)',

	'course.name': 'Назва',
	'course.format': 'Формат',
	'course.status': 'Статус',
	'course.startDate': 'Дата старту',
	'course.schedule': 'Розклад',
	'course.nominalPrice': 'Номінальна вартість (грн)',
	'course.lessonPrice': 'Ціна заняття (грн)',
	'course.billingModel': 'Режим оплати',
	'course.billingModel.per_lesson': 'за заняття',
	'course.billingModel.per_course': 'за курс наперед',
	'course.programMiroLink': 'Програма (Miro-посилання)',
	'course.description': 'Опис',
	'course.chatLink': 'Чат курсу (посилання)',
	'course.fop': 'ФОП (для згоди в анкеті)',
	'course.group': 'Група курсів',
	'course.curator': 'Куратор(ка)',
	// salary-2 (spec § 2.2): клас вводиться на КУРСІ, заняття успадковує
	'course.salaryClass': 'Тарифний клас курсу',
	'course.instructor': 'Ведучий(а)',
	'course.groupSize': 'Розмір групи',
	'course.groupSize.small': 'мала',
	'course.groupSize.large': 'велика',
	'course.participants': 'Учасники курсу',
	'course.noParticipants': 'На курсі ще немає учасників.',

	'course.format.online': 'онлайн',
	'course.format.offline': 'офлайн',
	'course.format.trip': 'виїзд',
	'course.format.tour': 'тур',

	'course.status.upcoming': 'майбутній',
	'course.status.ongoing': 'триває',
	'course.status.finished': 'завершений',

	'participation.status': 'Статус участі',
	'participation.status.interested': 'зацікавився',
	'participation.status.form': 'анкета',
	'participation.status.considering': 'роздуми',
	'participation.status.requisites': 'реквізити',
	'participation.status.dropped': 'вибув',
	'participation.status.studying': 'навчається',
	'participation.status.finished': 'завершив',
	'participation.individualPrice': 'Індивідуальна ціна (грн)',
	'participation.individualPrice.per_lesson':
		'Індивідуальна ціна за заняття (грн)',
	'participation.individualPrice.per_course':
		'Індивідуальна ціна за курс (грн)',
	'participation.priceUnit.per_lesson': 'грн/заняття',
	'participation.add': 'Додати учасника',
	'participation.remove': 'Прибрати з курсу',

	'auth.loginTitle': 'З поверненням!',
	'auth.loginSubtitle':
		'Введіть свою електронну пошту — ми надішлемо код для входу.',
	'auth.email': 'Електронна пошта',
	'auth.sendCode': 'Надіслати код',
	'auth.verifyLoginTitle': 'Перевірте пошту',
	'auth.verifyLoginSubtitle': 'Ми надіслали код для входу на вашу адресу.',
	'auth.resendCode': 'Надіслати код ще раз',
	'auth.codeResent': 'Новий код надіслано — перевірте пошту.',
	'auth.signupTitle': 'Вітаємо!',
	'auth.loginWithGoogle': 'Увійти через Google',
	'auth.signupWithGoogle': 'Зареєструватись через Google',
	'auth.connectWithGoogle': 'Підключити Google',

	'onboarding.title': 'Ласкаво просимо, {email}!',
	'onboarding.subtitle': 'Заповніть, будь ласка, дані профілю.',
	'onboarding.username': 'Логін (username)',
	'onboarding.name': 'Ім’я',
	'onboarding.imageAlt': 'Фото профілю',
	'onboarding.agree':
		'Чи погоджуєтесь ви з умовами користування та політикою приватності?',
	'onboarding.remember': 'Запам’ятати мене',
	'onboarding.submit': 'Створити акаунт',

	'apply.title': 'Запис на курс',
	'apply.subtitle': 'Заповніть анкету — ми зв’яжемось із вами.',
	'apply.course': 'Курс',
	'apply.courses': 'Курси (оберіть один або кілька)',
	'apply.knowsChannels': 'Чи знаєте про ФБ і Telegram Петра?',
	'apply.comment': 'Коментар',
	'apply.consent': 'Погоджуюсь на обробку персональних даних ({fop})',
	'apply.consentNoFop': 'Погоджуюсь на обробку персональних даних',
	'apply.submit': 'Надіслати анкету',
	'apply.success': 'Анкету надіслано! Ми зв’яжемось із вами найближчим часом.',
	'apply.noCourses': 'Наразі немає відкритих курсів для запису.',
	'apply.loginHint': 'Вже реєструвались?',
	'apply.loginHintLink': 'Увійдіть — форма заповниться сама',
	'apply.prefillNote': 'Поля підставлено з вашого акаунта.',
	'apply.sources': 'Звідки про нас дізнались?',

	// значення поля «Джерело» — спільні для анкети /apply і картки адміна
	'source.telegram': 'Telegram',
	'source.facebook': 'Facebook',
	'source.instagram': 'Instagram',
	'source.tiktok': 'TikTok',
	'source.youtube': 'YouTube',
	'source.friend_referral': 'Надіслали знайомі',

	'applications.title': 'Анкети',
	'applications.empty': 'Поки що жодної анкети.',
	'applications.board': 'Воронка анкет',
	'applications.unassigned': 'нікому не призначено',

	'application.title': 'Анкета',
	'application.sources': 'Джерело',
	'application.status': 'Статус',
	// статуси воронки v2 — вербатим переліку кураторки (батч 2 Б1)
	'application.status.new': 'Нова',
	'application.status.contacted': 'Написали',
	'application.status.prepayment': 'Передоплата',
	'application.status.paid_course': 'Оплачено курс',
	'application.status.subsidized': 'Пільгово',
	'application.status.plans_to_pay': 'Планує оплатити',
	'application.status.waiting_list': 'Лист очікування',
	'application.status.invoice': 'Рахунок',
	'application.status.declined': 'Відмова',
	'application.inGroup': 'в групі',
	'application.assignee': 'Хто веде',
	'application.disposition': 'Результат першого контакту',
	'application.disposition.requisites_given': 'надали реквізити',
	'application.disposition.pay_later': 'оплата потім',
	'application.disposition.will_pay_on_date':
		'«такого-то числа зможу» → нагадати',
	'application.disposition.invite_next_cohort': 'запросити на наступний набір',
	'application.disposition.refusal': 'відмова',
	'application.disposition.no_response': 'не відповідає',
	'application.disposition.link_issued': 'лінк видано',
	'application.disposition.curator_introduced': 'куратора представлено',
	'application.paymentAmount': 'Сума оплати (грн)',
	'application.takeOver': 'Взяти собі',
	'application.takenBy': 'Веде: {name}',
	'application.alreadyTaken': 'Цю анкету вже взяв(ла) {name}',
	'application.history': 'Історія',
	'application.addNote': 'Додати нотатку',
	'application.consentLabel': 'Згода (ФОП)',
	'application.clientType.new': '🆕 новий клієнт',
	'application.clientType.returning': '⭐ постійний учасник',
	'application.wasSubsidized': '🎁 був(ла) пільгово',
	// м'яка підказка кураторці (батч 3 В3, вербатим «м'яка підказка чудово»):
	// з постійних передоплату НЕ беруть (жива Coda-практика). Суто сигнал —
	// жодного enforcement статусів/форм. Апостроф — U+2019 як у решті UA-текстів
	'application.prepaymentHint':
		'💡 Постійна учасниця — передоплата не обов’язкова',
	// людиночитні лейбли kind-ів історії подій анкети (рендеряться у стовпці
	// історії; detail лишається сирим текстом події, окремо)
	'application.event.submitted': 'Анкету подано',
	'application.event.assigned': 'Призначено',
	'application.event.status_changed': 'Зміна статусу',
	'application.event.disposition_set': 'Результат контакту',
	'application.event.payment_noted': 'Оплата',
	'application.event.note': 'Нотатка',
	// «Рахунок» = B2B-кейс. Г2-корекція (батч 4): кураторка позначає потребу
	// рахунку і передає контакт платника корп-кураторці; реквізити/ФОП —
	// пізніший етап корп-кураторки, тому опційні
	'application.event.invoice_requested': 'Запит на формування рахунку',
	'application.invoice.heading': 'Рахунок для роботодавця',
	'application.invoice.contact': 'Контакт платника (для корп-кураторки)',
	'application.invoice.optionalHint':
		'Поля нижче — етап корп-кураторки, опційно',
	'application.invoice.requisites': 'Реквізити платника',
	'application.invoice.fop': 'ФОП для рахунку',
	'application.invoice.courseFopHint': 'ФОП курсу: {fop}',
	'application.invoice.lastRequest': 'Останній запит',
	'application.invoice.submit': 'Запит на рахунок',

	// docgen-1: конфіг ФОПів-виконавців (/settings/fops) — дані, не код (#79);
	// видалення нема — деактивація (spec § 2.6)
	'fops.title': 'ФОПи-виконавці',
	'fops.new': 'Новий ФОП',
	'fops.empty': 'Поки що жодного ФОПа-виконавця — додайте першого.',
	'fops.actions': 'Дії',
	'fop.name': 'ПІБ ФОПа',
	'fop.inn': 'ІПН',
	'fop.iban': 'IBAN',
	'fop.bank': 'Банк',
	'fop.certificate': '№ свідоцтва / витягу',
	'fop.taxStatusLine': 'Рядок податкового статусу (як у шаблоні)',
	'fop.address': 'Адреса',
	'fop.kved': 'КВЕД',
	'fop.signerName': 'Підписант («Прізвище І. П.»)',
	// docgen-3: реквізити договору (опційні per-ФОП)
	'fop.phone': 'Телефон (для реквізитів договору)',
	'fop.edrBasisClause': 'Підстава діяльності (клауза ЄДР для договору)',
	'fop.status': 'Статус',
	'fop.active': 'активний',
	'fop.inactive': 'неактивний',
	'fop.deactivate': 'Деактивувати',
	'fop.activate': 'Активувати',

	// salary-1: тарифні класи занять (spec § 2.2) — людські лейбли ключів
	// app/domain/salary.ts salaryClassKeys
	'salary.class.online': 'Заняття онлайн',
	'salary.class.offline': 'Заняття офлайн',
	'salary.class.offline_snack': 'Заняття офлайн з перекусом',
	'salary.class.offline_two_snacks_or_5h':
		'Заняття офлайн з двома перекусами або 5+ годин',
	// salary-1: ставки поза класами занять (spec §§ 2.1, 2.5, 2.6)
	'salary.rate.participant': 'За кожного учасника заняття',
	'salary.rate.application': 'За обробку нової анкети на курс',
	'salary.rate.screencaster_recording': 'За запис заняття (скрінкастерці)',
	// instructor-comp-spec § 2.7: людська назва рядка компенсації ведучого —
	// i18n-ключ (дріт несе `kind`, текст живе тут); термінологія § 9 — «ведучих»
	'salary.rate.instructorComp': 'Компенсація ведучого',
	// salary-1: поверхня ставок (/settings/salary-rates) — edit-only (§ 2.3)
	'salaryRates.title': 'Ставки нарахування',
	'salaryRates.intro':
		'Сім фіксованих ставок у цілих гривнях. Ключі не створюються і не видаляються — редагуються лише суми. Порожнє поле означає «не чіпати цей ключ»: незаповнена ставка світиться прогалиною у звіті і блокує фіксацію відомості, а очищення вже заповненого поля ставку НЕ скасовує — щоб прибрати суму, її треба змінити. Нуль — свідоме значення ставки, а не «не налаштовано».',
	'salaryRates.rate': 'Ставка',
	'salaryRates.amount': 'Сума, грн',
	'salaryRates.updatedAt': 'Оновлено',
	'salaryRates.updatedBy': 'Хто міняв',
	'salaryRates.notConfigured': 'не налаштовано',
	// salary-2: живий місячний звіт (/salary) — spec §§ 3 пп. 3-4, 6
	'salary.report.title': 'Нарахування за місяць',
	'salary.report.intro':
		'Живий розрахунок: рядків у базі не створює, правки відвідуваності й тарифних класів видно одразу. Показані всі отримувачки місяця — кураторки занять, скрінкастерки та акторки оброблених анкет. Прогалини показані окремо і в підсумок не входять.',
	'salary.report.month': 'Місяць',
	'salary.report.show': 'Показати',
	'salary.report.recipient': 'Отримувачка',
	'salary.report.lessons': 'Занять',
	'salary.report.participants': 'Учасників',
	'salary.report.recordings': 'Записів',
	'salary.report.applications': 'Анкет',
	'salary.report.total': 'Разом, грн',
	'salary.report.monthTotal': 'Разом за місяць, грн',
	'salary.report.empty': 'За цей місяць нарахувань і прогалин немає.',
	'salary.report.gaps': 'Прогалини місяця',
	'salary.report.fixationBlocked': 'фіксація заблокована',
	// § 2.8: рядок місяця з чинною відомістю береться зі statement — позначка
	// каже, що сума вже зафіксована, а не порахована наживо
	'salary.report.fixed': 'зафіксовано',
	// прогалини — формулювання одне на всі поверхні звіту (§§ 2.4, 3)
	'salary.gap.lessonsWithoutClass':
		'занять без тарифного класу: {lessons} (притримано учасник-нарахувань: {withheld})',
	'salary.gap.lessonsNotYetHeld': 'занять місяця ще не проведено: {lessons}',
	'salary.gap.lessonsWithoutCurator': 'занять без кураторки: {lessons}',
	'salary.gap.applicationsWithoutAttribution':
		'анкет без атрибутовної обробки: {applications} (без акторки: {nullActor}, без типізованої події: {untyped})',
	'salary.gap.rateNotConfigured': 'ставка не налаштована: {keys}',
	// salary-2: детектор OQ-B6 (§ 2.6) — не нарахування, а видима підозра
	'salary.warn.maybeSecondCurator': 'Можливо, друга кураторка не зазначена',
	'salary.warn.lesson':
		'{date}, {course} — {curator}: учасників-спожили {participants}',
	// salary-2: деталізація отримувачки (§ 3 п.4)
	'salary.detail.title': 'Деталізація: {name}',
	'salary.detail.back': '← до звіту за місяць',
	'salary.detail.date': 'Дата',
	'salary.detail.course': 'Курс',
	'salary.detail.kind': 'За що',
	'salary.detail.rate': 'Ставка, грн',
	'salary.detail.participants': 'Учасників',
	'salary.detail.participantsCell': '{participants} × {rate}',
	'salary.detail.amount': 'Сума, грн',
	'salary.detail.empty': 'Нарахувань за цей місяць немає.',
	// salary-3: фіксація відомості + carry-forward (spec §§ 2.4, 2.8, 3 п.5)
	'salary.fix.button': 'Зафіксувати відомість',
	'salary.fix.notYetHeld':
		'Занять місяця ще не проведено: {lessons} — відомість зафіксується без них, дельта дожене наступним місяцем',
	'salary.fix.blocked':
		'Фіксація заблокована: не всі ставки налаштовані (див. прогалини місяця у звіті)',
	'salary.statement.title': 'Зафіксована відомість',
	'salary.statement.fixedAt': 'Відомість зафіксовано {date} — {name}',
	'salary.statement.recipientName': 'Отримувачка у відомості: {name}',
	// rename юзера історію не переписує — показуємо ОБИДВА імені (§ 2.4 meta)
	'salary.statement.recipientRenamed':
		'Отримувачка у відомості: {name} (зараз: {current})',
	'salary.statement.hint':
		'Поточний розрахунок відрізняється від зафіксованого — дельта увійде в наступну (незафіксовану) відомість',
	'salary.statement.void': 'Анулювати',
	'salary.statement.voidConfirm': 'Точно анулювати?',
	'salary.statement.voidedRow':
		'Анульована відомість: зафіксована {fixedDate}, анульована {voidedDate} ({voidedBy}), разом {total} грн',
	// instructor-comp-spec § 6.1 (voided-канал): та сама відомість БЕЗ суми —
	// шар, від якого зріз зняв частину рядків, часткового «разом» не бачить
	'salary.statement.voidedRowNeutral':
		'Анульована відомість: зафіксована {fixedDate}, анульована {voidedDate} ({voidedBy})',
	// instructor-comp-spec § 2.7: підрядок у клітинці отримувачки — «разом» несе
	// компонент, а власної колонки в нього нема (шари `amount` і `full`)
	'salary.report.instructorComp': 'у т.ч. компенсація ведучого: {amount} грн',
	// умови домовленості — ЛИШЕ шар `full` (§ 2.7), значення ЗАМОРОЖЕНІ зі
	// снапшота відомості, не з сьогоднішньої домовленості
	'salary.instructor.percentTerms':
		'{percent}% від {base}, вага {weight}% · прихід {income} грн, витрати: авто {auto} грн + ручні {manual} грн',
	'salary.instructor.baseIncome': 'приходу',
	'salary.instructor.baseProfit': 'прибутку',
	'salary.instructor.fixedCourseTerms':
		'фіксовано за курс {amount} грн, вага {weight}%',
	'salary.instructor.fixedLessonTerms':
		'фіксовано за заняття {amount} грн × {lessons}, вага {weight}%',
	'salary.carry.title': 'Перенесення з попередніх місяців',
	'salary.carry.from': 'перенесення з {month}',
	'salary.carry.reportLine': 'перенесення з попередніх місяців: {amount} грн',

	// instructor-comp IC-1b (spec §§ 6, 9 п.2): грант-поверхня прав на
	// компенсації ведучих у профілі юзера — owner-only. УВАГА: людські назви
	// САМИХ ролей тут НЕ живуть — вони приходять із `Role.description` у БД
	// (міграція ic1a, ратифікована термінологія «…ведучих», § 9), і копія тут
	// розійшлася б із наданим правом мовчки
	'compRights.section': 'Права на компенсації ведучих',
	// друге речення називає, ЩО САМЕ незворотне (GDPR-рулінг #136): не акаунт, а
	// сам факт-запис. Формулювання СВІДОМО каже «журнал більше не блокує
	// видалення акаунта», а не «акаунт видаляється будь-коли»: останнє було б
	// неправдою — на User живуть ще два Restrict-ребра (відомості, факт ведення
	// курсу), і обіцянка з цієї поверхні ловилася б відмовою на іншій.
	// Перше речення теж кваліфіковане: ПОІМЕННО журнал відповідає, лише поки
	// акаунт існує; після erasure лишається анонімний факт — і власник має
	// бачити цю межу ДО кліку
	'compRights.intro':
		'Надає і знімає лише власник. Кожна зміна пишеться в журнал (кому, яку роль, хто і коли) — щоб пізніше можна було відповісти, хто мав доступ до умов компенсацій у конкретному місяці; поіменно — лише поки акаунт існує, після його видалення лишається анонімний факт. Незворотний сам запис у журналі, а не акаунт: журнал більше не блокує видалення акаунта — після видалення (людина робить його у своїх налаштуваннях) рядок лишається знеособленим: «у час T актор такої-то ролі надав або зняв таке-то право».',
	'compRights.granted': 'надано',
	'compRights.notGranted': 'не надано',
	// рядок, у якому право вже є через ІНШУ роль (сьогодні лише owner): «не
	// надано» тут було б неправдою про доступ, який у людини вже є
	'compRights.coveredByOwner': 'має право через роль owner',
	'compRights.grant': 'Надати',
	// підтвердження гранту (useDoubleCheck): перший клік озброює, другий надає
	'compRights.grantConfirm': 'Точно надати?',
	'compRights.revoke': 'Зняти',

	// docgen-2: рахунки (/invoices) — черга запитів, форма створення, сторінка
	// рахунку, завантаження docx (spec §§ 3, 10.2). Вкладка «Реєстр»,
	// оплачено/анулювання — docgen-3
	// (`nav.invoices` живе вгорі, разом з рештою nav-ключів)
	'invoices.title': 'Рахунки',
	'invoices.tabs': 'Вкладки рахунків',
	'invoices.requestsTab': 'Запити',
	'invoices.registryTab': 'Реєстр',
	'invoices.requests.empty': 'Черга порожня — нових запитів на рахунок нема.',
	'invoices.requests.requestText': 'Текст запиту',
	'invoices.requests.create': 'Створити рахунок',
	'invoice.new.title': 'Новий рахунок',
	'invoice.new.requestReference': 'Запит (довідково)',
	'invoice.new.course': 'Курс',
	'invoice.fop': 'ФОП-виконавець',
	'invoice.fop.placeholder': '— оберіть ФОП —',
	'invoice.customerName': 'Замовник (назва)',
	'invoice.customerSignerName': 'Підписант замовника («в особі …»)',
	'invoice.customerDetails': 'Реквізити платника',
	'invoice.contractNumber': '№ договору',
	'invoice.contractDate': 'Дата договору',
	'invoice.addendumNumber': '№ додаткової угоди',
	'invoice.stage': 'Етап',
	'invoice.stage.prepayment': 'передоплата',
	'invoice.stage.postpayment': 'доплата',
	'invoice.stage.full': 'повна',
	'invoice.stage.other': 'інше',
	'invoice.amount': 'Сума, грн',
	'invoice.amountValue': '{amount} грн',
	'invoice.number': 'Номер рахунку',
	'invoice.numberHint':
		'авто-номер за серією ФОПа; змінений вручну — ручний номер бухгалтера',
	// показується, коли дата рахунку зрушена з сьогодні, а номер лишався
	// prefill-ом: поле очищене, номер присвоїться при збереженні (§ 2.4)
	'invoice.numberFromIssueDate':
		'номер буде присвоєно за датою рахунку при збереженні',
	'invoice.issueDate': 'Дата рахунку',
	'invoice.serviceDate': 'Дата надання послуги',
	'invoice.validUntil': 'Дійсний до оплати до',
	'invoice.serviceDescription': 'Найменування послуги',
	'invoice.title': 'Рахунок № {number}',
	'invoice.noFile': 'без файла',
	'invoice.regenerate': 'Перегенерувати',
	'invoice.files': 'Згенеровані файли',
	'invoice.files.empty': 'Файл ще не згенеровано.',
	'invoice.download': 'Завантажити',
	'invoice.status': 'Статус',
	'invoice.status.issued': 'виставлений',
	'application.event.invoice_issued': 'Рахунок виставлено',

	// docgen-3: реєстр, лайфсайкл (§ 2.6), акт (§ 3.2), договір (§ 3.3),
	// нагадування про акт (§ 2.7 — текст НАЗИВАЄ конфіг-тригер)
	'invoice.status.paid': 'оплачений',
	'invoice.status.void': 'анульований',
	'invoice.paidAt': 'Дата оплати',
	'invoice.voidedAt': 'Дата анулювання',
	'invoice.lifecycle': 'Дії з рахунком',
	'invoice.markPaid': 'Оплачено',
	'invoice.void': 'Анулювати',
	'invoice.voidConfirm': 'Точно анулювати?',
	'invoice.generate': 'Генерація документів',
	'invoice.generateAct': 'Згенерувати акт',
	'invoice.generateContract': 'Згенерувати договір',
	'invoices.registry.filter.all': '— всі —',
	'invoices.registry.filter.month': 'Місяць',
	'invoices.registry.filter.apply': 'Фільтрувати',
	'invoices.registry.filter.reset': 'Скинути фільтри',
	'invoices.registry.totals': 'Підсумок по фільтру: {count} рах., {amount} грн',
	'invoices.registry.empty': 'Рахунків за цим фільтром нема.',
	'invoices.registry.act': 'Акт',
	'invoices.registry.actDone': 'акт ✓',
	'invoices.registry.actReminder.service_date_passed':
		'заняття проведено, акт не сформований',
	'invoices.registry.actReminder.invoice_paid':
		'рахунок оплачено, акт не сформований',
	'act.title': 'Акт до рахунку № {number}',
	'act.partiesHint':
		'Сторони беруться з рахунку: виконавець — зі снапшота реквізитів ФОПа, замовник — з полів рахунку.',
	'act.relatedInvoices': 'Рахунки за цим договором (довідково)',
	'act.relatedInvoicesHint':
		'Той самий ФОП і № договору; суму акта введіть самостійно — авто-сумування нема.',
	'act.date': 'Дата складання',
	'act.amount': 'Сума акта, грн',
	'act.contractRefClause': 'Посилання на договір (клауза)',
	'act.generate': 'Згенерувати акт',
	'contract.title': 'Договір до рахунку № {number}',
	'contract.effectiveDate': 'Дата вступу в силу',
	'contract.addendumDate': 'Дата додаткової угоди',
	'contract.contractEndDate': 'Діє до',
	'contract.snapshotIncompleteHint':
		'Реквізити виконавця неповні — договір міститиме пропуски; поповни ФОП-конфіг і перевипусти рахунок.',
	'contract.customerIntroClause': 'Інтро-клауза замовника',
	'contract.rows': 'Деталізація додаткової угоди',
	'contract.rows.detail': 'Деталізація послуг',
	'contract.rows.paymentDue': 'Строк оплати',
	'contract.rows.deliveryDue': 'Строк виконання',
	'contract.rows.price': 'Ціна, грн (без ПДВ)',
	'contract.rows.add': 'Додати рядок',
	'contract.rows.remove': 'Прибрати рядок {number}',
	'contract.total': 'Разом',
	'contract.generate': 'Згенерувати договір',

	'payments.title': 'Оплати',
	'payments.add': 'Записати оплату',
	'payments.none': 'Оплат ще немає.',
	'payment.amount': 'Сума (грн)',
	'payment.type': 'Тип',
	'payment.type.advance': 'аванс',
	'payment.type.full': '100%',
	'payment.type.free_of_charge': 'пільгово (=0)',
	'payment.type.discount': 'знижка',
	'payment.payer': 'Платник',
	'payment.payer.self': 'сам(а)',
	'payment.payer.employer': 'роботодавець',
	'payment.payer.gift': 'подарунок',
	'payment.note': 'Примітка',
	'payment.lesson': 'Заняття (опційно)',
	'payment.wholeCourse': 'за курс цілком',
	'payment.forLesson': 'заняття {date}',
	'payment.paidAt': 'Дата оплати',
	'payment.paid': 'оплачено',
	'payment.unpaid': 'не оплачено',
	'payment.paidTotal': 'Оплачено разом',
	'payment.accruedPaidDebt':
		'нараховано {accrued} / сплачено {paid} / борг {debt}',
	'payment.audit': 'Історія змін',
	'payment.void': 'Скасувати оплату',
	'payment.voidConfirm': 'Точно скасувати?',
	'payment.voidedInfo': 'скасовано — {name}, {date}',

	'lessons.title': 'Заняття',
	'lessons.none': 'Занять ще немає — додайте перше.',
	'lessons.numbered': 'Заняття {number}',
	'lessons.date': 'Дата',
	'lessons.lessonTitle': 'Назва (опційно)',
	'lessons.add': 'Додати заняття',
	'lesson.screencaster': 'Скрінкастер(ка)',
	// salary-2 (spec § 2.2): ефективний клас = власний ?? курсовий; форма
	// показує І значення, І його джерело — інакше «звідки це» не видно
	'lesson.salaryClass': 'Тарифний клас',
	'lesson.salaryClass.fromCourse': 'з курсу',
	'lesson.salaryClass.own': 'власний',
	'lesson.salaryClass.effective': 'Тарифний клас: {class} ({source})',
	'lesson.salaryClass.unset': 'Тарифний клас: не задано',
	'lesson.salaryClass.none': 'не задано',
	'attendance.title': 'Відвідуваність',
	'attendance.cellLabel': 'Відвідуваність: {name} — {date}',
	'attendance.unmarked': '—',
	'attendance.present': 'був/була',
	'attendance.absent': 'не був/не була',
	'attendance.recording': 'отримав(ла) запис',

	'video.title': 'Назва відео',
	'video.url': 'Посилання (URL)',
	'video.filePath': 'Файл у відео-сторі (локальне)',
	'video.local': 'локальний файл',
	'video.xorHint':
		'Заповніть рівно одне: посилання на зовнішнє відео АБО шлях файла у відео-сторі (data/videos/).',
	'video.add': 'Додати відео',
	'video.none': 'Відео ще не додані.',
	'video.access.request': 'Попросити доступ до записів',
	'video.access.requested': 'Доступ надано — записи нижче.',
	'video.access.none':
		'Записи цього курсу стануть доступні після запиту — натисніть кнопку.',
	'video.access.grants': 'Доступи до відео',
	'video.access.grant': 'Надати доступ',
	'video.access.grantedVia.request': 'за запитом',
	'video.access.grantedVia.manual': 'вручну',
	'chat.invite': 'Приєднатись до чату курсу',
	'chat.inviteHint': 'Посилання на чат з’явиться після оплати.',
	'chat.inChat': 'в чаті',
	'chat.paidNotInChat': 'оплатив(ла), але не в чаті',

	'hints.title': 'Підказки для куратора',
	'hints.noPressure':
		'Внутрішній дайджест — без тиску: система нічого не надсилає сама, куратор дивиться і вирішує.',
	'hints.none': 'Підказок немає — все під контролем.',
	'hints.onlyMine': 'лише мої',
	// стан фільтра в доступному імені (a11y): вимкнено vs увімкнено
	'hints.onlyMineOff': 'лише мої',
	'hints.onlyMineOn': 'лише мої (увімкнено)',
	'hints.trigger': 'Тригер',
	'hints.since': 'з {date}',
	'hints.lastActivity': 'останній рух: {date}',
	'hints.lessonAt': 'заняття: {date}',
	'hints.shownOf': 'показано {shown} з {total} — найстаріші перші',
	'hints.shownOfRecent': 'показано {shown} з {total} — новіші перші',
	'hints.shownOfSoonest': 'показано {shown} з {total} — найближчі перші',
	'hints.debt': 'борг {debt} грн (оплачено {paid} з {price})',
	'hints.firstContactDue.title': 'Перший контакт прострочено',
	'hints.firstContactDue.trigger':
		'анкета призначена понад {hours} год тому, результат першого контакту не заповнено',
	'hints.paymentDue.title': 'Очікується оплата',
	'hints.paymentDue.trigger': 'навчається, оплачено менше за нараховане',
	'hints.paidNotInChat.title': 'Оплатили, але не в чаті',
	'hints.paidNotInChat.trigger':
		'оплата є, а позначки «в чаті» немає — запросити в чат курсу',
	'hints.missedTwice.title': 'Пропущені заняття',
	'hints.missedTwice.trigger':
		'навчається, позначено «не був/не була» на 2 і більше заняттях',
	'hints.missedCount': 'пропущено занять: {count}',
	'hints.afterLesson.title': 'Після заняття',
	'hints.afterLesson.trigger':
		'заняття щойно відбулося — надішліть у чат лінк на запис і матеріали (Міро-дошка тощо) за наявності',
	'hints.upcomingLesson.title': 'Наступне заняття',
	'hints.upcomingLesson.trigger':
		'нагадайте групі про заняття і зробіть у чаті голосування про готовність долучитися',
	'hints.upcomingLesson.horizon.hour': 'за годину',
	'hints.upcomingLesson.horizon.tomorrow': 'завтра',
	'hints.upcomingLesson.horizon.threeDays': 'за 3 дні',
	'hints.upcomingLesson.horizon.week': 'за тиждень',

	'templates.title': 'Шаблони повідомлень',
	'templates.noAutoSend':
		'Шаблони — для копіювання вручну: система нічого не надсилає сама.',
	'templates.empty': 'Шаблонів ще немає — створіть перший.',
	'templates.new': 'Новий шаблон',
	'templates.renderTitle': 'Повідомлення з шаблону',
	'templates.selectTemplate': 'Шаблон',
	'templates.copy': 'Скопіювати',
	'templates.copied': 'Скопійовано!',
	'templates.copyFailed':
		'Не вдалося скопіювати автоматично — виділіть і скопіюйте текст вручну.',
	'templates.noneHint': 'Шаблонів ще немає — створіть у розділі:',
	'template.name': 'Назва',
	'template.body': 'Текст шаблону',
	'template.placeholdersHint':
		'Плейсхолдери: {{учасник}}, {{курс}}, {{ціна}}, {{борг}}, {{розклад}}',

	'commlog.title': 'Історія комунікацій',
	'commlog.none': 'Записів ще немає.',
	'commlog.add': 'Додати запис',
	'commlog.channel': 'Канал',
	'commlog.note': 'Нотатка',
	'commlog.channel.messenger': 'месенджер',
	'commlog.channel.phone': 'телефон',
	'commlog.channel.email': 'email',
	'commlog.channel.in_person': 'особисто',
	'commlog.channel.other': 'інше',

	'action.create': 'Створити',
	'action.save': 'Зберегти',
	'action.edit': 'Редагувати',
	'action.cancel': 'Скасувати',
	'action.delete': 'Видалити',
	'action.deleteConfirm': 'Точно видалити?',

	'error.forbidden': 'У вас немає доступу до цієї сторінки.',

	// server-side form errors (schema factories + action formErrors/fieldErrors)
	'error.apply.consentRequired': 'Згода на обробку даних обовʼязкова',
	'error.application.invoiceContactRequired':
		'Вкажіть контакт платника для корп-кураторки',
	'error.application.invoiceStatusStale':
		'Анкета вже не у статусі «Рахунок» — оновіть сторінку',
	'error.apply.courseRequired': 'Оберіть хоча б один курс',
	'error.apply.courseUnavailable': 'Курс недоступний для запису',
	'error.apply.partialFailure':
		'Не вдалося подати анкету на: {courses}. Спробуйте ще раз — уже створені анкети не дублюються.',
	'error.bulk.statusRequired': 'Для зміни статусу вкажіть статус',
	'error.participantNotFound': 'Учасника не знайдено',
	'error.courseNotFound': 'Курс не знайдено',
	'error.fopNotFound': 'ФОПа не знайдено',
	'error.fopToggleInvalid': 'Некоректна дія зі статусом ФОПа',
	'error.compRoleInvalid': 'Некоректна дія з правом на компенсації ведучих',
	// salary-1 (spec § 2.3, інваріант «а»): читання ставок fail-closed —
	// невідомий ключ у БД не ігнорується мовчки, а називається
	'error.salaryRates.unknownKey':
		'Невідомий ключ ставки в базі: {keys}. Поверхня налаштувань редагує лише сім відомих ключів — зайвий рядок треба прибрати з даних.',
	'toast.salaryRates.saved': 'Ставки збережено: {count}',
	'toast.salaryRates.unchanged': 'Змін не було',
	// salary-2: місяць звіту відбивається на межі, а не підмінюється тихо
	'error.salary.badMonth':
		'Некоректний місяць: «{month}». Очікується формат РРРР-ММ.',
	'error.salary.recipientNotFound': 'Отримувачку не знайдено',
	// instructor-comp-spec § 6 (варіант А, ратифіковано 2026-08-08): відомість із
	// компонентом ведучого фіксує/анулює лише той, хто його бачить. Текст несе
	// рівно причину — ні суми, ні відсотка (§ 6.1)
	'error.salary.compRightRequired':
		'Ця відомість потребує додаткового права на компенсації ведучих',
	// salary-3: typed-помилки фіксації/анулювання (§§ 2.3 «в», 2.4)
	'error.salary.alreadyFixed': 'Відомість за цей місяць вже зафіксована',
	'error.salary.fixRatesIncomplete':
		'Фіксація заблокована: не налаштовано ставки — {keys}',
	'error.salary.nothingToFix':
		'Немає чого фіксувати: у місяці нема ні нарахувань, ні прогалин, ні перенесень',
	// два РІЗНІ стани — не одне повідомлення: «уже анульовано» і «такої
	// відомості за цей місяць у цієї отримувачки нема» (звужений void, § 2.4)
	'error.salary.voidAlready': 'Відомість уже анульовано',
	'error.salary.voidNotFound':
		'Відомість не знайдено серед відомостей цієї отримувачки за цей місяць',
	// § 2.8 fail-closed: зіпсований фін-снапшот — названа відмова з id, а не
	// частковий рендер і не голий 500
	'error.salary.corruptStatement':
		'Зафіксована відомість пошкоджена (id: {id}) — дані снапшота не читаються. Показ і перерахунок зупинено.',
	// Restrict-ребра на User — typed-відмова замість 500 на видаленні акаунта.
	// Ребер ДВА: salary-spec § 4 відомості й instructor-comp-spec § 2.5 факт
	// ведучого (журнал грантів вибув — GDPR-рулінг #136 замінив його ребро на
	// SetNull). Підстава цих двох — ЛІН СЕСІЇ, НЕ рулінг: бухгалтерське
	// зберігання (Art 17(1)(a) + 6(1)(f), резерв 17(3)(e) — erasure-spec
	// § 3.3; 17(3)(b) не цитуємо: покриває лише право ЄС/держави-члена і
	// обґрунтовує збереження ЗАПИСУ, а не відмову у видаленні акаунта); обидва
	// лишаються кандидатами на ту саму SetNull-форму — окремий розгляд при
	// GDPR-пасі. Текст
	// називає КЛАС причини: перелік «саме через відомості» був би неправдою для
	// другого ребра. Перелік — ІЛЮСТРАТИВНИЙ («наприклад»), і саме тому: P2003 не
	// каже, ЯКЕ ребро спрацювало, тож закритий список читався б як «спрацювало
	// все перелічене», а третє майбутнє ребро зробило б його ще й неповним
	// мовчки. Ключ зветься `deleteKeptHistory`, а не `…FinancialHistory`: факт
	// ведення курсу — не фінансова історія
	'error.user.deleteKeptHistory':
		'Неможливо видалити акаунт: із ним звʼязана історія, яку система не стирає (наприклад: зафіксовані відомості нарахувань, факти ведення курсів)',
	'toast.salary.fixed': 'Відомість зафіксовано',
	'toast.salary.voided': 'Відомість анульовано',
	'error.invoiceNotFound': 'Рахунок не знайдено',
	'error.invoiceVoided': 'Рахунок анульовано — генерація документів недоступна',
	'error.invoice.fopRequired': 'Оберіть ФОП-виконавця',
	'error.invoice.fopUnavailable':
		'Обраний ФОП недоступний (неактивний або видалений)',
	'error.invoice.numberTaken': 'Номер {number} вже використано у цього ФОПа',
	'error.invoice.invalidDate': 'Некоректна дата (такого дня в місяці немає)',
	'toast.invoice.created': 'Рахунок {number} створено',
	'toast.invoice.createdNoFile':
		'Рахунок {number} створено, але файл не згенеровано — перегенеруйте на сторінці рахунку',
	'toast.invoice.createdNoFileDeterministic':
		'Рахунок {number} створено, але файл не згенеровано (помилка шаблона) — потрібне втручання, перегенерація не зарадить',
	'toast.invoice.regenerated': 'Файл перегенеровано',
	'toast.invoice.regenerateFailed':
		'Не вдалося згенерувати файл — спробуйте ще раз',
	'toast.invoice.regenerateFailedDeterministic':
		'Не вдалося згенерувати файл — помилка шаблона, перегенерація не зарадить',
	// docgen-3: лайфсайкл (§ 2.6) + генерація акта/договору (§§ 3.2-3.3)
	'error.invoice.payBlocked':
		'Рахунок не позначено оплаченим: він уже оплачений або анульований',
	'error.invoice.voidBlocked': 'Рахунок уже анульовано',
	'toast.invoice.paid': 'Рахунок {number} позначено оплаченим',
	'toast.invoice.voided': 'Рахунок {number} анульовано',
	'toast.act.generated': 'Акт згенеровано',
	'toast.act.failed': 'Не вдалося згенерувати акт — спробуйте ще раз',
	'toast.act.failedDeterministic':
		'Не вдалося згенерувати акт — помилка шаблона чи даних, повтор не зарадить',
	'toast.contract.generated': 'Договір згенеровано',
	'toast.contract.failed': 'Не вдалося згенерувати договір — спробуйте ще раз',
	'toast.contract.failedDeterministic':
		'Не вдалося згенерувати договір — помилка шаблона чи даних, повтор не зарадить',
	'error.participant.emailExists': 'Учасник із цим email вже існує',
	'error.participant.notEnrolled': 'Учасник не записаний на цей курс',
	'error.payment.voidedNoEdit':
		'Скасовану оплату не можна редагувати — запишіть нову',
	'error.payment.alreadyVoided': 'Оплату вже скасовано',
	'error.payment.notFound': 'Оплату не знайдено',
	'error.lesson.wrongCourse': 'Заняття не належить цьому курсу',
	'error.lesson.hasRecords':
		'Заняття має позначки відвідуваності ({attendance}) або оплати ({payments}) — видалення заблоковано',
	'error.lesson.assigneeNotStaff': 'Обрана людина не є персоналом',
	'error.lessonOrParticipantWrongCourse':
		'Заняття або учасник не належать цьому курсу',
	'error.video.exactlyOne':
		'Вкажіть рівно одне: посилання (URL) або шлях файла у відео-сторі',
	'error.videoAccess.notEnrolled':
		'Учасник не зарахований на курс — спершу додайте запис на курс',
	'error.template.nameExists': 'Шаблон із такою назвою вже існує',

	'error.import.headerAndDataRequired':
		'Потрібен рядок заголовків і хоча б один рядок даних',
	'error.import.nameColumnMissing':
		'Не знайдено колонку імені (ім’я / піб / name)',
	'error.import.emptyName': 'Порожнє ім’я — рядок пропущено',
	'error.import.noAnchor':
		'«{name}»: немає анкора (email або телефон) — рядок пропущено',
	'error.import.anchorConflict':
		'«{name}»: email і телефон вказують на РІЗНИХ наявних учасників — звірте вручну; рядок пропущено',
	'error.import.phoneAmbiguous':
		'«{name}»: телефон збігається з кількома учасниками — уточніть email; рядок пропущено',
	'error.import.rowFailed': '«{name}»: помилка запису — рядок пропущено',

	'error.inbox.noTab': 'Немає табуляції (потрібно: сума ⇥ призначення)',
	'error.inbox.noAmount': 'Сума не розпізнана',
	'error.inbox.badAmountFormat':
		'Некоректний формат суми (лише число, опційно валюта грн/₴/uah)',
	'error.inbox.conflict': 'Цю оплату вже розібрано — оновіть сторінку',
	'error.inbox.badRequest': 'Некоректний запит — спробуйте ще раз',

	// user-validation schemas (reachable via Google onboarding)
	'error.username.required': 'Вкажіть логін',
	'error.username.tooShort': 'Логін закороткий',
	'error.username.tooLong': 'Логін задовгий',
	'error.username.invalid':
		'Логін може містити лише латинські літери, цифри та підкреслення',
	'error.username.taken': 'Користувач із таким логіном вже існує',
	'error.name.required': 'Вкажіть ім’я',
	'error.name.tooShort': 'Ім’я закоротке',
	'error.name.tooLong': 'Ім’я задовге',
	'error.email.required': 'Вкажіть email',
	'error.email.invalid': 'Email некоректний',
	'error.email.tooShort': 'Email закороткий',
	'error.email.tooLong': 'Email задовгий',
	'error.password.required': 'Вкажіть пароль',
	'error.password.tooShort': 'Пароль закороткий',
	'error.password.tooLong': 'Пароль задовгий',
	'error.password.mustMatch': 'Паролі мають збігатися',
	'error.agreeRequired':
		'Потрібна згода з умовами користування та політикою приватності',

	// server toasts
	'toast.apply.title': 'Дякуємо!',
	'toast.apply.description': 'Анкету надіслано.',
	'toast.bulk.title': 'Масова дія',
	'toast.bulk.enrolled':
		'Записано на курс: {created} нових (вже були: {existing})',
	'toast.bulk.statusUpdated': 'Статус оновлено: {updated} (вибрано {selected})',
	'toast.application.taken.title': 'Зайнято',
	'toast.signup.welcome.title': 'Вітаємо!',
	'toast.signup.welcome.description': 'Дякуємо за реєстрацію!',
	'toast.auth.failed.title': 'Помилка входу',
	'toast.auth.failed.description':
		'Сталася помилка під час автентифікації через {provider}.',
	'toast.connection.already.title': 'Вже підключено',
	'toast.connection.alreadyYours':
		'Ваш акаунт {provider} «{username}» вже підключено.',
	'toast.connection.alreadyOther':
		'Акаунт {provider} «{username}» вже підключено до іншого акаунта.',
	'toast.connection.connected.title': 'Підключено',
	'toast.connection.connected.description':
		'Ваш акаунт {provider} «{username}» підключено.',
	'toast.connection.initiatorMismatch':
		'Сесія змінилася під час підключення. Спробуйте ще раз.',

	'common.language': 'Мова',
	'common.loading': 'Завантаження…',
	'common.notSet': '—',
} as const

export type TranslationKey = keyof typeof uk
